import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Modal,
  Spin,
  Alert,
  Button,
  Descriptions,
  Slider,
  InputNumber,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import type { ImagingFileData } from "@/hooks/useImagingStudyFiles";
import type {
  CornerstoneImageLike,
  MetadataItem,
  SliceMetadata,
} from "./imagingFileViewer.types";
import { extractSliceMetadata, UNKNOWN } from "./sliceMetadata";
import { useImagingStudyFiles } from "@/hooks/useImagingStudyFiles";
import { downloadService } from "@/services/downloadService";
import CornerstoneViewport from "./CornerstoneViewport";

interface ImagingStudyImageViewerProps {
  imagingStudyId: string | null;
  onClose: () => void;
}

const VIEWPORT_HEIGHT = 480;
const PREFETCH = 4; // slices to preload on each side of the current one
const MAX_CACHED = 48; // cap on in-memory blob URLs (LRU)

function formatBytes(bytes: number | null): string {
  if (bytes == null) return UNKNOWN;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Endoscopy captures are served as MP4/WebM renditions rather than DICOM. */
function isVideoFile(file: ImagingFileData | undefined): boolean {
  return !!file?.content_type?.startsWith("video/");
}

function DetailGroup({
  title,
  items,
}: {
  title: string;
  items: MetadataItem[];
}) {
  if (items.length === 0) return null;
  return (
    <Descriptions
      size="small"
      column={1}
      bordered
      title={
        <span className="text-xs font-semibold text-[#2B4752]">{title}</span>
      }
      items={items.map(({ label, value }) => ({
        key: label,
        label,
        children: value,
      }))}
    />
  );
}

/**
 * What is on screen right now.
 *
 * "File" comes from the API row and is there even when the pixels are not; "Slice" and "Image"
 * are read from the DICOM the viewer just decoded, so they change as you scrub and are absent
 * for a slice that has no pixel data or is not DICOM at all. Study- and series-level attributes
 * are the same for every slice and belong in the Imaging Studies table, not here.
 */
function FileMetadata({
  file,
  slice,
}: {
  file: ImagingFileData;
  slice: SliceMetadata | null;
}) {
  const fileItems: MetadataItem[] = [
    { label: "File name", value: file.file_name ?? UNKNOWN },
    { label: "Content type", value: file.content_type ?? UNKNOWN },
    { label: "File size", value: formatBytes(file.file_size_bytes) },
    { label: "SOP instance UID", value: file.sop_instance_uid ?? UNKNOWN },
  ];
  Object.entries(file.custom_fields ?? {}).forEach(([key, value]) => {
    fileItems.push({ label: key, value: String(value ?? UNKNOWN) });
  });

  const hasSliceDetail =
    !!slice && (slice.slice.length > 0 || slice.image.length > 0);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <DetailGroup title="File" items={fileItems} />
      {hasSliceDetail ? (
        <>
          <DetailGroup title="Slice" items={slice.slice} />
          <DetailGroup title="Image" items={slice.image} />
        </>
      ) : (
        <p className="col-span-1 self-center text-xs text-gray-500 md:col-span-2">
          Per-slice DICOM detail appears once a slice with pixel data is
          displayed.
        </p>
      )}
    </div>
  );
}

/** Fixed-height frame so the layout never jumps between slices. */
function ViewerFrame({ children }: { children: ReactNode }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded bg-black"
      style={{ height: VIEWPORT_HEIGHT }}
    >
      {children}
    </div>
  );
}

function NoImageData({ fileName }: { fileName: string | null }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 border border-dashed border-gray-300 bg-gray-50">
      <PictureOutlined style={{ fontSize: 40 }} className="text-gray-400" />
      <p className="font-medium text-[#2B4752]">No image data</p>
      <p className="max-w-xs text-center text-xs text-gray-500">
        This slice is indexed, but its pixel data isn’t available in the imaging
        store. Nothing to display for {fileName ?? "this file"}.
      </p>
    </div>
  );
}

function ViewerArea({
  file,
  objectUrl,
  failed,
  onImage,
}: {
  file: ImagingFileData;
  objectUrl: string | null;
  failed: boolean;
  onImage: (image: CornerstoneImageLike | null) => void;
}) {
  const { file_url, content_type, file_name } = file;

  if (!file_url) return <NoImageData fileName={file_name} />;

  if (failed) {
    return (
      <ViewerFrame>
        <div className="absolute inset-0 flex items-center justify-center">
          <Alert
            type="error"
            message="Failed to load file. Please try again."
          />
        </div>
      </ViewerFrame>
    );
  }

  // DICOM: the viewport stays mounted + fixed-size; it shows its own overlay
  // spinner while the slice's blob is still arriving.
  if (content_type === "application/dicom") {
    return <CornerstoneViewport fileUrl={objectUrl} onImage={onImage} />;
  }

  // Video captures (endoscopy). The source captures are VP8-in-AVI and
  // WMV3/ASF, neither of which a browser plays as-is; ingestion now publishes
  // a web rendition of each (VP8 remuxed to WebM, WMV3 transcoded to H.264
  // MP4), so these play inline. The imaging API is otherwise
  // Bearer-authenticated, which a native <video src> can't attach — so for
  // video files only, the backend mints a short-lived, single-file token into
  // file_url itself (see ImagingVideoTokenAuthentication), making it directly
  // streamable with native Range support. No blob fetch, unlike DICOM/images.
  // ``preload="metadata"``, not ``"auto"``: these captures run 30-300 MB, and
  // "auto" pulls the whole file the moment the modal mounts — before anyone has
  // pressed play, and for every capture the reviewer merely clicks past. Fetching
  // just the header keeps opening a study cheap; the browser then streams on
  // demand over Range, so playback still starts in about a second.
  if (isVideoFile(file)) {
    return (
      <ViewerFrame>
        <video
          key={file_url}
          src={file_url}
          controls
          playsInline
          preload="metadata"
          className="absolute inset-0 m-auto max-h-full max-w-full"
        />
      </ViewerFrame>
    );
  }

  if (content_type?.startsWith("image/")) {
    return (
      <ViewerFrame>
        {objectUrl ? (
          <img
            src={objectUrl}
            alt={file_name ?? "Imaging file"}
            className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spin />
          </div>
        )}
      </ViewerFrame>
    );
  }

  return (
    <ViewerFrame>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-50">
        <p className="text-[#2B4752]">
          Preview not available for this file type.
        </p>
        {objectUrl && (
          <Button
            type="primary"
            href={objectUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download {file_name ?? "file"}
          </Button>
        )}
      </div>
    </ViewerFrame>
  );
}

/**
 * Stack navigation: prev/next arrows, a scrubber that loads the item you land on
 * (commits on release), a type-a-number box, and an N/total indicator. ``unit``
 * is what a step means for this study — a CT slice, or an endoscopy capture.
 */
function StackNavigator({
  index,
  total,
  onIndex,
  unit,
}: {
  index: number;
  total: number;
  onIndex: (i: number) => void;
  unit: string;
}) {
  const [live, setLive] = useState(index + 1);
  const [syncedIndex, setSyncedIndex] = useState(index);
  if (index !== syncedIndex) {
    setSyncedIndex(index);
    setLive(index + 1);
  }

  if (total <= 1) return null;

  return (
    <div className="flex items-center gap-3 px-1">
      <Button
        size="small"
        icon={<LeftOutlined />}
        disabled={index <= 0}
        onClick={() => onIndex(index - 1)}
        aria-label={`Previous ${unit}`}
      />
      <Slider
        className="flex-1"
        min={1}
        max={total}
        value={live}
        onChange={(v) => setLive(v as number)}
        onChangeComplete={(v) => onIndex((v as number) - 1)}
        tooltip={{ formatter: (v) => `${v} / ${total}` }}
      />
      <Button
        size="small"
        icon={<RightOutlined />}
        disabled={index >= total - 1}
        onClick={() => onIndex(index + 1)}
        aria-label={`Next ${unit}`}
      />
      <InputNumber
        size="small"
        min={1}
        max={total}
        value={live}
        onChange={(v) => {
          if (typeof v === "number") {
            setLive(v);
            onIndex(v - 1);
          }
        }}
        style={{ width: 72 }}
        aria-label={`Go to ${unit} number`}
      />
      <span className="whitespace-nowrap text-sm text-gray-500">
        {index + 1} / {total}
      </span>
    </div>
  );
}

export default function ImagingStudyImageViewer({
  imagingStudyId,
  onClose,
}: ImagingStudyImageViewerProps) {
  const { data, isLoading, isError } = useImagingStudyFiles(imagingStudyId);

  // Ordered by slice sequence (backend orders by series then instance number).
  const orderedFiles = useMemo(() => {
    const list = (data as ImagingFileData[] | undefined) ?? [];
    return [...list].sort(
      (a, b) =>
        (a.file_sequence_number ?? Number.MAX_SAFE_INTEGER) -
        (b.file_sequence_number ?? Number.MAX_SAFE_INTEGER),
    );
  }, [data]);

  const [index, setIndex] = useState(0);
  const [seenStudy, setSeenStudy] = useState(imagingStudyId);
  if (imagingStudyId !== seenStudy) {
    setSeenStudy(imagingStudyId);
    setIndex(0);
  }

  const total = orderedFiles.length;
  const currentIndex = total > 0 ? Math.min(index, total - 1) : 0;
  const selectedFile = orderedFiles[currentIndex] ?? null;

  // ---- slice blob cache + neighbour prefetch (avoids re-downloading on scrub) ----
  const cacheRef = useRef<Map<string, string>>(new Map());
  const inflightRef = useRef<Set<string>>(new Set());
  const failedRef = useRef<Set<string>>(new Set());
  const lruRef = useRef<string[]>([]);
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const load = useCallback(
    (file: ImagingFileData | undefined, protect: Set<string>) => {
      const id = file?.imaging_file_id;
      const url = file?.file_url;
      if (!id || !url) return;
      if (cacheRef.current.has(id)) {
        lruRef.current = lruRef.current.filter((x) => x !== id);
        lruRef.current.push(id);
        return;
      }
      if (inflightRef.current.has(id) || failedRef.current.has(id)) return;
      inflightRef.current.add(id);
      downloadService
        .createBlobUrlFromFile(url)
        .then((blob) => {
          inflightRef.current.delete(id);
          cacheRef.current.set(id, blob);
          lruRef.current.push(id);
          while (lruRef.current.length > MAX_CACHED) {
            const old = lruRef.current.shift();
            if (!old) break;
            if (protect.has(old)) {
              lruRef.current.push(old);
              continue;
            }
            const u = cacheRef.current.get(old);
            if (u) URL.revokeObjectURL(u);
            cacheRef.current.delete(old);
          }
          bump();
        })
        .catch(() => {
          inflightRef.current.delete(id);
          failedRef.current.add(id);
          bump();
        });
    },
    [],
  );

  // Drop the previous study's blobs when the study changes.
  useEffect(() => {
    const c = cacheRef.current;
    c.forEach((u) => URL.revokeObjectURL(u));
    c.clear();
    lruRef.current = [];
    inflightRef.current.clear();
    failedRef.current.clear();
  }, [imagingStudyId]);

  // Fetch the current slice + its neighbours whenever the position changes.
  useEffect(() => {
    if (total === 0) return;
    const protect = new Set<string>();
    for (let d = -PREFETCH; d <= PREFETCH; d++) {
      const f = orderedFiles[currentIndex + d];
      if (f?.imaging_file_id) protect.add(f.imaging_file_id);
    }
    // Video streams directly from its (tokenized) file_url — see ViewerArea —
    // so it's never blob-fetched/cached, current slice or neighbour alike.
    const current = orderedFiles[currentIndex];
    if (!isVideoFile(current)) load(current, protect);
    for (let d = 1; d <= PREFETCH; d++) {
      const next = orderedFiles[currentIndex + d];
      const prev = orderedFiles[currentIndex - d];
      if (!isVideoFile(next)) load(next, protect);
      if (!isVideoFile(prev)) load(prev, protect);
    }
  }, [currentIndex, orderedFiles, total, load]);

  // Revoke everything on unmount.
  useEffect(() => {
    const c = cacheRef.current;
    return () => {
      c.forEach((u) => URL.revokeObjectURL(u));
      c.clear();
    };
  }, []);

  // The decoded slice's own metadata. Keyed to the file it came from: on a scrub the panel goes
  // blank for the instant before the next slice decodes rather than describing the previous one.
  const [sliceMeta, setSliceMeta] = useState<{
    fileId: string;
    metadata: SliceMetadata;
  } | null>(null);

  const currentId = selectedFile?.imaging_file_id ?? null;
  const currentUrl = currentId
    ? (cacheRef.current.get(currentId) ?? null)
    : null;
  const currentFailed = currentId ? failedRef.current.has(currentId) : false;

  const handleImage = useCallback(
    (image: CornerstoneImageLike | null) => {
      if (!currentId) return;
      setSliceMeta(
        image
          ? { fileId: currentId, metadata: extractSliceMetadata(image) }
          : null,
      );
    },
    [currentId],
  );

  return (
    <Modal
      open={!!imagingStudyId}
      onCancel={onClose}
      footer={null}
      width={960}
      centered
      title="Imaging files"
      destroyOnHidden
    >
      {isLoading && (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      )}

      {isError && (
        <Alert
          type="error"
          message="Failed to load imaging files. Please try again."
          className="my-4"
        />
      )}

      {!isLoading && !isError && total === 0 && (
        <p className="py-8 text-center text-gray-500">
          No files associated with this imaging study.
        </p>
      )}

      {!isLoading && !isError && total > 0 && selectedFile && (
        <div className="mt-2 flex flex-col gap-4">
          <ViewerArea
            file={selectedFile}
            objectUrl={currentUrl}
            failed={currentFailed}
            onImage={handleImage}
          />
          <StackNavigator
            index={currentIndex}
            total={total}
            onIndex={setIndex}
            unit={isVideoFile(selectedFile) ? "capture" : "slice"}
          />
          <FileMetadata
            file={selectedFile}
            slice={
              sliceMeta?.fileId === selectedFile.imaging_file_id
                ? sliceMeta.metadata
                : null
            }
          />
        </div>
      )}
    </Modal>
  );
}
